import React, { ReactElement, useEffect, useState } from "react";
import styled from "styled-components";
import { useCombobox } from "downshift";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import Input from "../input";
import Chip, { ChipColors } from "../../layout/chip";
import { ToggleTipProps } from "../../layout/toggle_tip";

const TagsInputWrapper = styled.span`
  display: inline-block;
  width: 100%;
  margin-bottom: 12px;
  .wrapper {
    width: 100%;
    position: relative;
  }

  .text-input {
    margin-bottom: ${({ theme }) => theme.deprecatedSpacing.component.five};
  }
`;

const StyledTagsContainer = styled.div<{ hasError: boolean }>`
  width: 100%;
  min-height: ${({ theme }) => theme.deprecatedSpacing.component.nine};
  background-color: ${({ theme }) => theme.deprecatedColors.white};
  vertical-align: middle;
  border: 1px solid
    ${({ theme, hasError }) =>
      hasError ? theme.deprecatedColors.error : theme.deprecatedColors.gray.eight};
  border-radius: ${({ theme }) => theme.deprecatedSpacing.component.two};
  box-sizing: border-box;
  padding: ${({ theme }) => theme.deprecatedSpacing.component.five}
    ${({ theme }) => theme.deprecatedSpacing.component.four}
    ${({ theme }) => theme.deprecatedSpacing.component.three};
  display: flex;
  flex-wrap: wrap;

  & > * {
    margin: 0 ${({ theme }) => theme.deprecatedSpacing.component.two}
      ${({ theme }) => theme.deprecatedSpacing.component.three};
  }
`;

const StyledOptions = styled.ul<{ isOpen: boolean }>`
  overflow: hidden;
  background-color: ${({ theme }) => theme.deprecatedColors.primary.extraLight};
  border-radius: ${({ theme }) => theme.deprecatedBorder.radius.large};
  box-shadow: ${({ theme }) => theme.deprecatedElevation.two};
  padding: ${({ theme, isOpen }) => (isOpen ? theme.deprecatedSpacing.component.five : 0)};
  position: absolute;
  left: 0;
  right: 0;
  top: 90%;
  list-style-type: none;
  max-height: 130px;
  overflow-y: scroll;
  z-index: 1;
`;

const StyledOption = styled.li<{ isHighlighted: boolean; isDisabled?: boolean }>`
  border-radius: ${({ theme }) => theme.deprecatedBorder.radius.normal};
  padding: ${({ theme }) => theme.deprecatedSpacing.component.four};
  background-color: ${({ theme, isHighlighted }) =>
    isHighlighted ? theme.deprecatedColors.accent.six : "transparent"};
  color: ${({ theme, isHighlighted, isDisabled }) =>
    isDisabled
      ? theme.deprecatedColors.gray.seven
      : isHighlighted
      ? theme.deprecatedColors.white
      : theme.deprecatedColors.text.normal};
  cursor: ${({ isHighlighted, isDisabled }) =>
    isDisabled ? "not-allowed" : isHighlighted ? "pointer" : "normal"};
`;

//The label is optional when passed down as a prop
export interface TagItemType {
  value: string;
  label?: string;
}

//The label is required for the items used to generate the dropdown options
interface DropdownItemType {
  value: string;
  label: string;
}

export interface TagsProps {
  label?: string;
  helperText?: string;
  className?: string;
  error?: string;
  toggleTip?: ReactElement<ToggleTipProps>;
  adornmentIcon?: IconProp;
  availableTags: TagItemType[];
  createTagLabel?: string;
  id?: string;
  placeholder?: string;
  value?: string[];
  disableTagCreation?: boolean;
  description?: string;
  questionIsOptional?: boolean;
  onTagSelect: (value: string) => void;
  onTagCreate?: (value: string) => void;
  onTagDelete: (value: string) => void;
  disabled?: boolean;
  tagLabelPlural?: string;
}

export default React.memo(function Tags({
  value = [],
  onTagSelect,
  onTagCreate,
  label,
  helperText,
  className,
  error,
  toggleTip,
  adornmentIcon,
  placeholder,
  id,
  createTagLabel = "Create:",
  availableTags,
  disableTagCreation,
  description,
  questionIsOptional = false,
  onTagDelete,
  disabled = false,
  tagLabelPlural = "",
}: TagsProps) {
  //represents the entire list of tags available for the component
  const [formattedTags, setFormattedTags] = useState<DropdownItemType[]>([]);

  //represents the list of tags displayed in the dropdown which are filtered when the user types
  const [inputItems, setInputItems] = useState<DropdownItemType[]>([]);
  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    setInputValue,
    openMenu,
    closeMenu,
  } = useCombobox({
    items: inputItems,
    itemToString: (item) => (item ? item.label : ""),
    onSelectedItemChange: ({ selectedItem }) => {
      setInputValue("");

      //Checks if the selectedItemValue is not in the list of selected values
      if (
        selectedItem &&
        !valueIsSelected(selectedItem.value) &&
        !isCustomValue(selectedItem.value)
      ) {
        onTagSelect(selectedItem.value);
      }

      //Keeps track of the custom values entered by the user
      if (
        (disableTagCreation === false || disableTagCreation === undefined) &&
        selectedItem &&
        isCustomValue(selectedItem.value) &&
        !valueIsSelected(selectedItem.value) &&
        onTagCreate
      ) {
        onTagCreate(selectedItem.value);
      }
      closeMenu();
    },
    onInputValueChange: ({ inputValue }) => {
      //Filters the options that are displayed in the dropdown menu
      const itemsToSet = formattedTags.filter((option) => {
        return option.label.toLowerCase().includes(inputValue?.toLowerCase() ?? "");
      });

      const customInputItem =
        inputValue &&
        isCustomValue(inputValue) &&
        (disableTagCreation === false || disableTagCreation === undefined) //If value typed by the user is not in the list of options, it displays a new dropdown item to "create" the tag
          ? [{ label: `${createTagLabel} "${inputValue}"`, value: inputValue }]
          : [];

      setInputItems([...itemsToSet, ...customInputItem]);
    },
  });

  useEffect(() => {
    const getFormattedTags = () =>
      availableTags.map((availableTag) => ({
        label: availableTag?.label ?? availableTag.value,
        value: availableTag.value,
      }));

    setFormattedTags(getFormattedTags());
    setInputItems(getFormattedTags());
  }, [availableTags]);

  const availableTagsValues = () => availableTags.map((availableTag) => availableTag.value);

  const valueIsSelected = (valueToFind: string) => value?.some((item) => item === valueToFind);

  const handleTagDelete = (deletedItem: string) => {
    if (onTagDelete) {
      onTagDelete(deletedItem);
    }
  };

  const isCustomValue = (value: string) => {
    //This is used to check if the value selected was entered by the user and is not included in the list of predefined options
    return !availableTagsValues().includes(value);
  };

  return (
    <TagsInputWrapper className={className}>
      <div className="wrapper tags-selector">
        <Input
          {...getInputProps()}
          type={"text"}
          error={error}
          adornmentIcon={adornmentIcon}
          toggleTip={toggleTip}
          helperText={helperText}
          label={label}
          placeholder={placeholder}
          id={id}
          className={"text-input"}
          onFocus={openMenu}
          onClick={openMenu}
          onMouseDown={openMenu}
          data-test="tags-input"
          description={description}
          disabled={disabled}
          questionIsOptional={questionIsOptional}
        />
        <StyledOptions {...getMenuProps()} isOpen={isOpen}>
          {isOpen &&
            inputItems.map((item, index) => (
              <StyledOption
                {...getItemProps({ item, index })}
                isHighlighted={highlightedIndex === index}
                isDisabled={valueIsSelected(item.value)}
                data-test={`tag-option-${item.value}`}
                key={`${item}${index}`}
              >
                {item.label}
              </StyledOption>
            ))}
        </StyledOptions>
      </div>

      <StyledTagsContainer hasError={!!error} className="tags-container" data-test="tags-container">
        {value.map((selectedValue) => {
          const optionData = formattedTags.find(
            (option) => option.value === selectedValue,
          ) as DropdownItemType;
          return (
            <Chip
              key={selectedValue}
              label={optionData?.label ?? selectedValue}
              onDelete={!disabled ? () => handleTagDelete(selectedValue) : undefined}
              color={isCustomValue(selectedValue) ? ChipColors.two : ChipColors.six}
            />
          );
        })}
        {!value.length && <p>{`You haven't selected any ${tagLabelPlural}`}</p>}
      </StyledTagsContainer>
    </TagsInputWrapper>
  );
});
